/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useCallback, useEffect, useState } from 'react';
import appTheme from '../../common/theme/appTheme.json';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import CustomDataGrid from '../../common/data_grid/CustomDataGrid';
import { useHistory } from 'react-router-dom';
import { StyledProvisioningPageHeading } from '../../provisioning/StyledProvisioningPageHeading';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import CreateIcon from '@mui/icons-material/Create';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { RenderTopAlignedFixedHeightCell } from '../../common/data_grid/TopAlignedCell';
import { isNil } from 'lodash';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  selectAntennas,
  selectDeleteConfirmation,
  selectDeletingPlanError,
  selectDeletingPlanSuccess,
  selectLoadingAntennas,
  selectLoadingMissionPlans,
  selectMissionPlans,
  selectUserId,
} from '../../../store';
import { requestAntennas } from '../../../store/antennas/actions';
import { clearDeletedMissionError, clearDeletedMissionSuccess, clearSavedMissionPlan, clearSavedMissionPlanById, deleteMissionConfirmation, deleteMissionPlan, getMissionPlanById, getMissionPlans, setSelectedMissionPlanId } from '../../../store/missionPlans/actions';
import ModalDialog from '../../common/dialogs/ModalDialog';
import MissionPlan from '../../../shared/entities/missionPlan';
import { Dispatch } from 'redux';

interface PlansTableProps {
  // Required for displaying tables in unit tests
  disableVirtualization?: boolean;
}

export const handlePerformanceOutlookButton = (missionPlanId: number, history: any, dispatch: Dispatch) => {
  dispatch(setSelectedMissionPlanId(missionPlanId));
  history.push(`/plans/performance-outlook`);
};

const MissionPlansTable = (props: PlansTableProps) => {
  const { disableVirtualization } = props;
  const missionPlans = useSelector(selectMissionPlans, shallowEqual);
  const antennas = useSelector(selectAntennas, shallowEqual);
  const isRequestingMissionPlans = useSelector(selectLoadingMissionPlans, shallowEqual);
  const isRequestingAntennas = useSelector(selectLoadingAntennas, shallowEqual);
  const deleteModal = useSelector(selectDeleteConfirmation, shallowEqual);
  const [deleteModalTarget, setDeleteModalTarget] = useState<MissionPlan | undefined>(undefined);
  const userId = useSelector(selectUserId, shallowEqual);
  const history = useHistory();
  const dispatch = useDispatch();
  const [hideRightBtn, setHideRightBtn] = useState<boolean>(false);
  const [deleteClick, setDeleteClick] = useState<boolean>(false);

  const deletingError = useSelector(selectDeletingPlanError, shallowEqual);
  const deletingSuccess = useSelector(selectDeletingPlanSuccess, shallowEqual);
  const loadingPlans = isRequestingMissionPlans || isRequestingAntennas;
  const [deleteModalHeader, setDeleteHeader] = useState<string>('Confirm Mission Plan Deletion');
  const [deleteModalBody, setDeleteModalBody] = useState<JSX.Element>(
    <>
      Mission Plan will be deleted and all Mission Details and Flight Leg Data will be lost.
      <br />
      <br />
      This action cannot be undone.
    </>,
  );

  //Get mission plans when table mounts
  useEffect(() => {
    if (!isNil(userId)) {
      dispatch(getMissionPlans(null, userId));
      dispatch(clearSavedMissionPlan());
    }
  }, [dispatch, userId]);

  // Get antennas if they haven't been loaded yet, so that we can show antenna display names in the table instead of antenna name from the mission plan
  useEffect(() => {
    if (isNil(antennas)) {
      dispatch(requestAntennas());
    }
  }, [antennas, dispatch]);

  // Save mission plan in Redux store and redirect to mission details page
  const handleEditMissionClick = (userId: string, missionPlanId: number) => {
    dispatch(setSelectedMissionPlanId(missionPlanId));
    dispatch(getMissionPlanById(userId, missionPlanId));
    history.push(`/plans/mission-details`);
  };

  // Changes Delete text when the delete button is clicked
  useEffect(() => {
    setDeleteModalBody(
      <>
        Mission Plan '{deleteModalTarget?.name}' will be deleted and all Mission Details and Flight Leg Data will be
        lost.
        <br />
        <br />
        This action cannot be undone.
      </>,
    );
  }, [deleteClick, deleteModalTarget?.name]);

  // On successful deletion
  useEffect(() => {
    if (deletingSuccess) {
      dispatch(clearSavedMissionPlanById());
      dispatch(deleteMissionConfirmation(false));
      dispatch(clearDeletedMissionSuccess());
      if (userId) {
        dispatch(getMissionPlans(null, userId));
      }
    }
  }, [deletingSuccess, dispatch, history, userId]);

  // On unsuccessful deletion
  useEffect(() => {
    if (deletingError) {
      setHideRightBtn(true);
      dispatch(clearDeletedMissionError());
      setDeleteHeader('Something went wrong');
      setDeleteModalBody(<>Unable to delete mission plan. Please try again.</>);
    }
  }, [deletingError, dispatch]);

  const handleClickCallback = useCallback(() => {
    if (userId) {
      dispatch(getMissionPlanById(userId));
      history.push(`/plans/mission-details`);
    } // Error message will pop up on Mission Plans page if userId is null or undefined
  }, [history, userId, dispatch]);

  // Use Antenna Display Name for Antenna column if antennas have been loaded, otherwise fallback to antenna type from mission plan
  const getAntennaDisplayName = useCallback(
    (missionPlan: MissionPlan) => antennas?.[missionPlan.antennaTypeId]?.displayName || missionPlan.antennaType,
    [antennas],
  );

  const COLLAPSED_ROW_HEIGHT_PX = 49;
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Title',
      flex: 2,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'aircraftTailNumber',
      headerName: 'Tail Number',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'flightLegCount',
      headerName: 'Legs',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'origin',
      headerName: 'Origin',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'antennaType',
      headerName: 'Antenna',
      flex: 1,
      valueGetter: params => getAntennaDisplayName(params.row),
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'useHcxBeams',
      headerName: 'HCX',
      type: 'boolean',
      renderCell: params => {
        return params.value ? <p>Yes</p> : <p>No</p>;
      },
    },
    {
      field: 'creationDate',
      headerName: 'Date Created',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      flex: 1,
      align: 'right',
      cellClassName: 'ActionsCell-TopAligned',
      getActions: params => [
        <GridActionsCellItem
          key={'edit-plan'}
          icon={<CreateIcon />}
          onClick={() => {
            handleEditMissionClick(userId ? userId : '', params.row.id);
          }}
        />,
        <GridActionsCellItem
          key={'delete-plan'}
          icon={<DeleteIcon />}
          onClick={() => {
            //this value exists so that the useEffect triggers even if the delete target didn't change
            setDeleteClick(!deleteClick);
            setDeleteModalTarget(params.row);
            setHideRightBtn(false);
            dispatch(setSelectedMissionPlanId(params.row.id));
            dispatch(deleteMissionConfirmation(true));
          }}
        />,
        <GridActionsCellItem
          key={'view-plan'}
          icon={<ArrowForwardIcon />}
          onClick={() => {
            handlePerformanceOutlookButton(params.row.id, history, dispatch);
          }}
        />,
      ],
    },
  ];

  const AddPlanButton = useCallback(
    () => (
      <Button
        startIcon={<AddIcon />}
        variant="contained"
        sx={{
          marginLeft: 'auto',
          color: '#000000',
          letterSpacing: '0.8px',
          backgroundColor: appTheme.mptLightBlue,
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
            svg: {
              color: '#000000',
            },
          },
        }}
        onClick={handleClickCallback}
      >
        Create New Mission Plan
      </Button>
    ),
    [handleClickCallback],
  );

  return (
    <>
      <StyledProvisioningPageHeading>
        {' '}
        Mission Plans {missionPlans ? `(${missionPlans.length})` : ''}
      </StyledProvisioningPageHeading>

      {loadingPlans ? <ModalLoadingSpinner /> : null}

      <Box display="flex" justifyContent="space-between" flexDirection={'column'} height={'100%'}>
        <CustomDataGrid
          rows={loadingPlans ? [] : missionPlans || []} // Don't pass mission plans to table until antennas and plans are loaded
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => COLLAPSED_ROW_HEIGHT_PX}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'creationDate', sort: 'asc' }],
            },
          }}
          toolbarActionButtons={[<AddPlanButton key={'add-plans-button'} />]}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: 'Plans',
              },
            },
          }}
          disableVirtualization={disableVirtualization}
        />
      </Box>
      <ModalDialog
        open={deleteModal}
        leftBtnText="Cancel"
        rightBtnText="Delete"
        hideRightBtn={hideRightBtn}
        // cancel
        onLeftBtnClicked={() => {
          dispatch(deleteMissionConfirmation(false));
        }}
        // confirm delete
        onRightBtnClicked={() => {
          if (deleteModalTarget?.id && userId) {
            dispatch(deleteMissionPlan(deleteModalTarget?.id, userId));
          } else {
            dispatch(deleteMissionConfirmation(false));
            if (userId) {
              dispatch(getMissionPlans(null, userId));
            }
          }
        }}
        title={
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            <WarningIcon htmlColor={'#FFB029'} /> {deleteModalHeader}
          </Box>
        }
        message={deleteModalBody}
      />
    </>
  );
};

export default MissionPlansTable;
